import { useEffect, type KeyboardEvent, type RefObject } from 'react';

const FOCALISABLES =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Options {
  /** Appelé sur Échap. */
  onEchap: () => void;
  /** Sélecteur de l'élément qui reçoit le focus à l'ouverture (par défaut, le premier élément focalisable). */
  focusInitial?: string;
}

/**
 * Comportement commun des boîtes modales : focus placé dedans à l'ouverture, prisonnier pendant,
 * rendu à l'élément précédent à la fermeture, Échap. Retourne le gestionnaire de clavier à poser sur la boîte.
 */
export function useBoiteModale(boite: RefObject<HTMLElement | null>, options: Options): (e: KeyboardEvent<HTMLElement>) => void {
  useEffect(() => {
    const precedent = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const racine = boite.current;
    const cible =
      (options.focusInitial ? racine?.querySelector<HTMLElement>(options.focusInitial) : null) ??
      racine?.querySelector<HTMLElement>(FOCALISABLES) ??
      racine;
    cible?.focus();
    return () => precedent?.focus();
    // Ouverture et fermeture seulement : la boîte est montée quand elle est ouverte.
  }, []);

  return (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      options.onEchap();
      return;
    }
    if (e.key !== 'Tab') return;
    const focalisables = Array.from(boite.current?.querySelectorAll<HTMLElement>(FOCALISABLES) ?? []);
    const premier = focalisables[0];
    const dernier = focalisables[focalisables.length - 1];
    if (!premier || !dernier) {
      e.preventDefault();
      return;
    }
    const actif = document.activeElement;
    if (e.shiftKey && (actif === premier || actif === boite.current)) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && actif === dernier) {
      e.preventDefault();
      premier.focus();
    }
  };
}
